import { ORDER_METHODS, PaymentGateway, PaymentMethod } from '../services/exports/Constants';
import { CompanyDetailsInterface } from '../services/exports/Interfaces';

/**
 * A payment method can be hidden for the whole store (`'*'`) or only for specific
 * order methods (e.g. hide card on `pickup`). A method is hidden when it is flagged
 * with `'*'` OR with the order method currently selected for the order.
 *
 * Shared by every surface that lists payment methods — the selectable checkout list
 * (`PaymentFlowProvider`) and the basket's "accepted payment methods" strip — so they
 * stay consistent and a per-order-method hide can't be bypassed on one of them.
 * See FOO-3176.
 */
export const isPaymentMethodHiddenForOrderMethod = (
  hiddenPaymentMethods: CompanyDetailsInterface['hidden_payment_methods'] | undefined,
  paymentMethodId: PaymentMethod,
  orderMethod: ORDER_METHODS | undefined,
): boolean => {
  const hiddenFor: (ORDER_METHODS | string)[] = hiddenPaymentMethods?.[paymentMethodId] ?? [];

  return hiddenFor.includes('*') || (orderMethod != null && hiddenFor.includes(orderMethod));
};

/**
 * Adyen returns payment methods keyed by its own txVariant vocabulary (`scheme`,
 * `applepay`, …). Map the ones that correspond to a hideable app payment method so the
 * merchant's per-order-method hides also apply to the Adyen Drop-in, which otherwise
 * renders Adyen's full method list unfiltered. Adyen types with no mapping here (Klarna,
 * etc.) are never hidden by this check. See FOO-3176.
 */
export const ADYEN_TYPE_TO_PAYMENT_METHOD: Record<string, PaymentMethod> = {
  scheme: PaymentMethod.Card,
  applepay: PaymentMethod.ApplePay,
  googlepay: PaymentMethod.GooglePay,
  paypal: PaymentMethod.Paypal,
  paybybank: PaymentMethod.PayByBank,
};

/**
 * Whether an Adyen payment method (identified by its Adyen `type`) is hidden for the
 * given order method, per the merchant's `hidden_payment_methods` config.
 */
export const isAdyenPaymentMethodHiddenForOrderMethod = (
  hiddenPaymentMethods: CompanyDetailsInterface['hidden_payment_methods'] | undefined,
  adyenType: string | undefined,
  orderMethod: ORDER_METHODS | undefined,
): boolean => {
  const mapped = adyenType != null ? ADYEN_TYPE_TO_PAYMENT_METHOD[adyenType] : undefined;

  return mapped != null && isPaymentMethodHiddenForOrderMethod(hiddenPaymentMethods, mapped, orderMethod);
};

/**
 * What a backend that does not send `stripe_platform_payment_methods` charges on the platform: PayPal
 * (Stripe has no direct charges for it) and Apple Pay (moved off in 2023, when domains could not be
 * registered per connected account).
 */
const LEGACY_STRIPE_PLATFORM_PAYMENT_METHODS: PaymentMethod[] = [PaymentMethod.Paypal, PaymentMethod.ApplePay];

/**
 * Whether Stripe charges the method on the restaurant's own connected account (a direct charge), so
 * Stripe.js — wallet checks included — has to run as that account. The backend decides, per company;
 * the client only follows it, so a method can move accounts without a coordinated release.
 */
export const isStripeDirectPaymentMethod = (
  company: Pick<CompanyDetailsInterface, 'stripe_platform_payment_methods'> | null | undefined,
  paymentMethodId: PaymentMethod | null | undefined,
): boolean =>
  company?.stripe_platform_payment_methods
    ? !company.stripe_platform_payment_methods.includes(paymentMethodId)
    : !LEGACY_STRIPE_PLATFORM_PAYMENT_METHODS.includes(paymentMethodId);

/** The wallets as the Express Checkout Element names them and as Stripe reports them on a charge. */
export type StripeWallet = 'apple_pay' | 'google_pay' | 'link';

/**
 * The rows the Payment Element checkout still draws itself; the Element lists every other method
 * the restaurant's account can take. Cash and PayPal never were Stripe's to list (PayPal has no
 * direct charges), the card on file is confirmed with its stored id, and inside the native app the
 * wallets keep going through the app's bridge.
 */
export const isRenderedOutsideStripeElements = (
  paymentMethodId: PaymentMethod,
  context: { hasSavedCard: boolean; isMobileApp: boolean },
): boolean =>
  paymentMethodId === PaymentMethod.Cash ||
  paymentMethodId === PaymentMethod.Paypal ||
  (paymentMethodId === PaymentMethod.Card && context.hasSavedCard) ||
  (context.isMobileApp && (paymentMethodId === PaymentMethod.ApplePay || paymentMethodId === PaymentMethod.GooglePay));

/**
 * The enum case for a payment method type as Stripe names it, the wallet behind a card deciding
 * first (a wallet payment is a `card` to Stripe). A Klarna the Element confirmed is Klarna; a type
 * without a case is StripeOther, never a non-Stripe case. Mirrors the backend's
 * PaymentMethod::fromStripePaymentMethodType, which derives the same answer from the type.
 */
export const paymentMethodFromStripeType = (
  type: string | null | undefined,
  wallet?: StripeWallet | null,
): PaymentMethod | null => {
  if (wallet === 'apple_pay') {
    return PaymentMethod.ApplePay;
  }

  if (wallet === 'google_pay') {
    return PaymentMethod.GooglePay;
  }

  if (wallet === 'link' || type === 'link') {
    return PaymentMethod.Link;
  }

  if (!type) {
    return null;
  }

  const known = (Object.values(PaymentMethod) as string[]).includes(type) ? (type as PaymentMethod) : null;

  if (known === null || known === PaymentMethod.Cash || known === PaymentMethod.PaypalStandalone) {
    return PaymentMethod.StripeOther;
  }

  return known;
};

const STRIPE_WALLET_METHODS: PaymentMethod[] = [PaymentMethod.ApplePay, PaymentMethod.GooglePay, PaymentMethod.Link];

/**
 * The hidden methods the Elements can exclude by type, when the backend does not say
 * (CompanyResource.stripe_hideable_payment_method_types, mirrored from config/stripe.php). Anything
 * else a merchant hides is a row of the shop's own (cash, PayPal), a wallet (switched off on the
 * Elements' wallet options), or a type Stripe.js would reject on the exclusion list.
 */
const DEFAULT_HIDEABLE_STRIPE_TYPES: string[] = [
  'card',
  'ideal',
  'bancontact',
  'eps',
  'p24',
  'blik',
  'twint',
  'klarna',
  'pay_by_bank',
];

/**
 * The methods that pay by handing the customer to another app (a custom URL scheme or an Android
 * intent) and back by a return_url. The native app's WebView loads those links itself and fails on
 * them (`ERR_UNKNOWN_URL_SCHEME`), and even when the other app opens the way back lands in the system
 * browser, so the app does not offer them until it handles the hand-off. Every one is a type Stripe.js
 * takes on the exclusion list (checked against the live Stripe.js, which throws on an unknown one).
 */
const APP_HANDOFF_STRIPE_TYPES: string[] = [
  'twint',
  'mobilepay',
  'revolut_pay',
  'cashapp',
  'wechat_pay',
  'alipay',
  'swish',
  'vipps',
  'amazon_pay',
  'satispay',
];

/**
 * What the Elements must not offer for this checkout: the backend's delayed-notification list, PayPal
 * (a row of the shop's own on the platform account; an Element row for it would map onto that row and
 * fold the Element), and the methods the merchant hides for the order method, which the backend also
 * excludes on the intent and refuses at checkout (StripePaymentElementMethods). A gift card has no
 * order method, so only what is hidden everywhere applies to it, plus what it can never be paid with.
 * Inside the native app, the methods that hand off to another app go too (APP_HANDOFF_STRIPE_TYPES).
 * Wallets cannot go on the type list (Stripe rejects it); they are switched off on the Elements' own
 * wallet options instead.
 */
export const stripeElementsExclusions = (
  company:
    | Pick<
        CompanyDetailsInterface,
        'hidden_payment_methods' | 'stripe_excluded_payment_method_types' | 'stripe_hideable_payment_method_types'
      >
    | null
    | undefined,
  orderMethod: ORDER_METHODS | undefined,
  alsoUnavailable: PaymentMethod[] = [],
  isMobileApp = false,
): { excludedPaymentMethodTypes: string[]; hiddenWallets: StripeWallet[] } => {
  const hidden = Array.from(
    new Set([
      ...(Object.keys(company?.hidden_payment_methods ?? {}) as PaymentMethod[]).filter((method) =>
        isPaymentMethodHiddenForOrderMethod(company?.hidden_payment_methods, method, orderMethod),
      ),
      ...alsoUnavailable,
    ]),
  );
  const hideable = company?.stripe_hideable_payment_method_types ?? DEFAULT_HIDEABLE_STRIPE_TYPES;

  const excludedPaymentMethodTypes = Array.from(
    new Set([
      ...(company?.stripe_excluded_payment_method_types ?? []),
      'paypal',
      ...hidden.filter((method) => hideable.includes(method)).map((method) => method as string),
      ...(isMobileApp ? APP_HANDOFF_STRIPE_TYPES : []),
    ]),
  );

  // Apple Pay and Google Pay are charged as type `card`: where the card is hidden they go with it, or
  // the backend refuses their card at checkout.
  const hiddenWalletMethods = hidden.includes(PaymentMethod.Card)
    ? [...hidden, PaymentMethod.ApplePay, PaymentMethod.GooglePay]
    : hidden;

  return {
    excludedPaymentMethodTypes,
    hiddenWallets: Array.from(
      new Set(hiddenWalletMethods.filter((method) => STRIPE_WALLET_METHODS.includes(method))),
    ) as unknown as StripeWallet[],
  };
};

/**
 * Stripe Google Pay is not offered inside the native mobile app. The app hands Stripe wallet
 * payments to the native Stripe SDK, and native Google Pay fails for every white-label app with
 * error 405 (the apps aren't registered in the Google Pay console). Stripe's web Google Pay isn't
 * supported inside a WebView either. Adyen's Google Pay is untouched.
 * See FOODAMIGOS-ONLINE-SHOP-MOBILE-APP-4S.
 */
export const isStripeGooglePayInMobileApp = (
  paymentMethodId: PaymentMethod | undefined,
  gateway: PaymentGateway | null,
  isMobileApp: boolean,
): boolean => isMobileApp && gateway === PaymentGateway.Stripe && paymentMethodId === PaymentMethod.GooglePay;
